import React, { useId } from 'react';

import {
  controlClassName,
  fieldErrorClasses,
  fieldHintClasses,
  fieldLabelClasses,
  type ControlSize,
} from './fieldStyles';

export { controlClassName };
export type { ControlSize };

export type FieldControlProps = {
  id: string;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-errormessage'?: string;
  'aria-invalid'?: true;
};

export type FieldProps = {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Hides the label visually but keeps it for assistive technology. */
  hideLabel?: boolean;
  /** Supplies a stable id when a form contract or test needs one. */
  id?: string;
  disabled?: boolean;
  className?: string;
  children: (props: FieldControlProps) => React.ReactNode;
};

/**
 * Label, hint, and error wrapper.
 *
 * The wiring is the point: `htmlFor`, `aria-describedby`, and `aria-invalid` are
 * generated here so no call site has to remember them. Hand-rolled fields across the
 * app connect the label but almost never the error message, which leaves screen
 * reader users hearing an invalid field with no explanation.
 *
 * `children` receives the ids to spread onto the control.
 */
export function Field({
  label,
  hint,
  error,
  hideLabel = false,
  id: suppliedId,
  disabled = false,
  className = '',
  children,
}: FieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? `field-${generatedId}`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`} data-disabled={disabled || undefined}>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : fieldLabelClasses}>
        {label}
      </label>
      {children({
        id,
        disabled: disabled || undefined,
        'aria-describedby': describedBy,
        'aria-errormessage': error ? errorId : undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {hint && !error ? (
        <p id={hintId} className={fieldHintClasses}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className={fieldErrorClasses}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: ControlSize;
  invalid?: boolean;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid = false, className = '', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={controlClassName({ size, invalid: invalid || props['aria-invalid'] === true || props['aria-invalid'] === 'true', className })}
      {...props}
    />
  );
});

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  size?: ControlSize;
  invalid?: boolean;
};

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { size = 'md', invalid = false, className = '', ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={controlClassName({ size, invalid: invalid || props['aria-invalid'] === true || props['aria-invalid'] === 'true', className: `min-h-24 ${className}` })}
      {...props}
    />
  );
});

export type SelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
  size?: ControlSize;
  invalid?: boolean;
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid = false, className = '', children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={controlClassName({ size, invalid: invalid || props['aria-invalid'] === true || props['aria-invalid'] === 'true', className: `pr-8 ${className}` })}
      {...props}
    >
      {children}
    </select>
  );
});

export default Field;
